import React from "react";
import {OVERFLOW_TABLE_WIDTH, PROPERTIES, STRIVE_ESCROW_TRACKING} from "../../consts/tables";
import Input from "../Input";
import Select from "../Select";
import {Link} from "react-router";
import arrowDown from "../../svgs/arrow_down.svg";
import arrowRight from "../../svgs/arrow_right.svg";
import Alert from "../Alert";
import {dateISOtoUS, dateUStoISO} from "../../date-converter";
import {addCommasToNumber} from "../../utils";
import moment from "moment";
import request from "arcdynamic-request";
import {ESCROW_TRACKING, ESCROW_TRACKING_LIST, HSC_TRACKING, HSC_TRACKING_LIST} from "../../consts/queries";
import {tasks} from "../EscrowTracking/EscrowTracking";
import {executeQuery} from "../../requests/executeQuery";
import SellerDrawer from "../SellerDrawer/SellerDrawer";
import BuyerDrawer from "../BuyerDrawer/BuyerDrawer";

let cloneDeep = require('lodash.clonedeep');
let findIndex = require('lodash.findindex');

function convertDatesToUS(dateProps, dataToConvert) {
    const data = cloneDeep(dataToConvert);

    Object.keys(data).forEach(row => {
        dateProps.forEach(key => {
            const str = dateISOtoUS(data[row][key]);
            if (str !== false) {
                data[row][key] = str;
            }
        });
    });

    return data;
}

function convertDatesToISO(dateProps, dataToConvert) {
    const data = cloneDeep(dataToConvert);

    Object.keys(data).forEach(row => {
        dateProps.forEach(key => {
            const str = dateUStoISO(data[row][key]);
            if (str !== false) {
                data[row][key] = str;
            }
        });
    });

    return data;
}

function convertCheckboxValueToNumber(checkboxProps, data) {
    const newData = cloneDeep(data);

    Object.keys(newData).forEach(row => {
        checkboxProps.forEach(key => {
            if (newData[row][key]) {
                newData[row][key] = parseInt(newData[row][key], 10);
            }
        });
    });

    return newData;
}

function convertCheckboxValueToString(checkboxProps, data) {
    const newData = cloneDeep(data);

    Object.keys(newData).forEach(row => {
        checkboxProps.forEach(key => {
            newData[row][key] = newData[row][key] ? '1' : '0';
        });
    });

    return newData;
}

function convertPrice(priceProps, data) {
    const newData = cloneDeep(data);

    Object.keys(newData).forEach(row => {
        priceProps.forEach(key => {
            if (newData[row][key]) {
                newData[row][key] = '$' + addCommasToNumber(newData[row][key]);
            }
        });
    });

    return newData;
}

function convertPercentages(percentProps, data) {
    const newData = cloneDeep(data);

    Object.keys(newData).forEach(row => {
        percentProps.forEach(key => {
            if (newData[row][key]) {
                newData[row][key] = newData[row][key] + '%';
            }
        });
    });

    return newData;
}

function getBuyerName(property) {
    const buyerName = [];

    if (property.BUYER_FIRST_NAME || property.BUYER_LAST_NAME) {
        if (property.BUYER_FIRST_NAME) {
            buyerName.push(property.BUYER_FIRST_NAME);
        }
        if (property.BUYER_LAST_NAME) {
            buyerName.push(property.BUYER_LAST_NAME);
        }
    } else {
        if (property.BUYER_COMPANY_NAME) {
            buyerName.push('*' + property.BUYER_COMPANY_NAME);
        }
    }

    return buyerName.join(' ');
}

function getBuyingAgentName(property) {
    let buyingAgentName = null;

    if (property.AGENT_FIRST_NAME || property.AGENT_LAST_NAME) {
        buyingAgentName = `${property.AGENT_FIRST_NAME} ${property.AGENT_LAST_NAME}`;
    }
    if (property.TEAM_FIRST_NAME || property.TEAM_LAST_NAME) {
        buyingAgentName = `${property.TEAM_FIRST_NAME} ${property.TEAM_LAST_NAME}`;
    }

    return buyingAgentName;
}

function getSellerName(property) {
    const sellerName = [];

    if (property.SELLER_FIRST_NAME || property.SELLER_LAST_NAME) {
        if (property.SELLER_FIRST_NAME) {
            sellerName.push(property.SELLER_FIRST_NAME);
        }
        if (property.SELLER_LAST_NAME) {
            sellerName.push(property.SELLER_LAST_NAME);
        }
    }

    return sellerName.join(' ');
}

function getAgentData(data) {
    const newData = cloneDeep(data);

    newData.forEach(row => {
        row.BUYER_NAME = getBuyerName(row);
        row.BUYING_AGENT_NAME = getBuyingAgentName(row);
        row.SELLER_NAME = getSellerName(row);
    });

    return newData;
}

function getBorrowerName(data) {
    const newData = cloneDeep(data);

    newData.forEach(row => {
        switch (row.LOAN_TYPE) {
            case 'Purchase':
                row.BORROWER_NAME = getBuyerName(row);
                break;
            case 'Construction':
            case 'Refi':
                row.BORROWER_NAME = getSellerName(row);
                break;
            default:
                row.BORROWER_NAME = '';
        }
        delete row.BUYER_FIRST_NAME;
        delete row.BUYER_LAST_NAME;
        delete row.SELLER_FIRST_NAME;
        delete row.SELLER_LAST_NAME;
    });

    return newData;
}

export function convertToTableFormat({dateProps, checkboxProps, priceProps, percentProps, data, hasAgentData = false, hasBorrower = false}) {
    let newData = convertDatesToUS(dateProps, data);
    newData = convertCheckboxValueToNumber(checkboxProps, newData);
    newData = convertPrice(priceProps, newData);
    if (percentProps)
        newData = convertPercentages(percentProps, newData);
    if (hasAgentData)
        newData = getAgentData(newData);
    if (hasBorrower)
        newData = getBorrowerName(newData);
    return newData;
}

export function convertToBackendFormat({dateProps, checkboxProps, data}) {
    let newData = convertDatesToISO(dateProps, data);
    newData = convertCheckboxValueToString(checkboxProps, newData);
    return newData;
}

function calculateDate(effectiveDate, period, extension) {
    let daysToAdd = 0;
    let date = moment(effectiveDate);
    date.add(period + extension, 'days');

    // If weekend, roll to the next business day
    if (date.day() === 0) {  // If day is Sunday
        daysToAdd = 1;
    }
    if (date.day() === 6) {  // If day is Saturday
        daysToAdd = 2;
    }

    if (daysToAdd > 0) {
        date.add(daysToAdd, 'days');
    }

    return date.format('MM/DD/YYYY');
}

function compare(obj1, obj2) {
    const date1 = new Date(obj1.FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE).getTime() >= new Date().getTime() + 24 * 60 * 60 * 1000 ?
        obj1.FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE : obj1.CLOSING_DATE;
    const date2 = new Date(obj2.FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE).getTime() >= new Date().getTime() + 24 * 60 * 60 * 1000 ?
        obj2.FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE : obj2.CLOSING_DATE;

    if (date1 > date2) {
        return 1;
    } else if (date1 < date2) {
        return -1;
    }
    return 0;
}

// Sort by Feasibility Period and Closing Date
function sort(data) {
    if (!data) return false;
    const sortedData = cloneDeep(data);
    return sortedData.sort(compare);
}

const width = 100 - 14 * 4;
const headerCellHeight = 71;
const cellHeight = 47.5;
const headerCellHeightSmall = 65;
const cellHeightSmall = 44.5;
const limitWidth = 992;

export default class Tracking extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            data: null,
            original: null,
            isBusy: null,
            error: null,
            success: null,
            width: 1400,
            collapsed: {},
            checkboxes: {
                'NEGOTIATION_CAP_RATE': true,
                'BUYING_COMPANY_STATE': true,
                'EFFECTIVE_DATE': true,
                'FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE': true,
            },
            areAllCollapsed: false,
            title: '',
            addNewItemType: null,
            addNewItemKey: null,
            newBuyer: null,
            newSeller: null,
            index: null,
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.handleCollapse = this.handleCollapse.bind(this);
        this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
        this.addNewItem = this.addNewItem.bind(this);
        this.savedNewItem = this.savedNewItem.bind(this);
        this.closeDrawer = this.closeDrawer.bind(this);
    }

    static initCollapsing(tasks) {
        const collapsed = {};
        tasks.map(task => {
            collapsed[task.id] = false;
        });
        return collapsed;
    }

    fetch({propertyId, dateProps, checkboxProps, priceProps, percentProps, title, saved = false}) {
        const state = {
            data: null,
            original: null,
            error: null,
            success: saved ? 'Saved' : null,
        };

        if (!saved) {
            state.title = title;
            state.collapsed = Tracking.initCollapsing(tasks);
        }

        this.setState(state, () => {
            const isHsc = this.state.title && this.state.title.toUpperCase().includes('HSC');
            let queryName;
            const param = {};
            if (propertyId) {
                queryName = isHsc ? HSC_TRACKING : ESCROW_TRACKING;
                param.PROPERTY_ID = propertyId;
            } else {
                queryName = isHsc ? HSC_TRACKING_LIST : ESCROW_TRACKING_LIST;
            }
            this.fetchTrackingData({queryName, param, dateProps, checkboxProps, priceProps, percentProps, isHsc});
        });
    }

    fetchTrackingData({queryName, param, dateProps, checkboxProps, priceProps, percentProps, isHsc}) {
        executeQuery({queryName, params: param}).then(res => {
            const data = isHsc ? res.data : sort(res.data);
            this.setState({
                isBusy: null,
                data: res && Array.isArray(res.data) && res.data.length ? convertToTableFormat({dateProps, checkboxProps, priceProps, percentProps, data, hasAgentData: !isHsc, hasBorrower: isHsc}) : false,
                original: res && Array.isArray(res.data) && res.data.length ? cloneDeep(data) : false
            });
        });
    }

    handleCollapse(e, taskId, tasks) {
        const {collapsed, checkboxes} = this.state;
        let areAllCollapsed = this.state.areAllCollapsed;
        const checkboxKeys = Object.keys(checkboxes);
        let found = false;

        if (taskId) {
            checkboxes[taskId] = !checkboxes[taskId];
        } else {
            Object.keys(checkboxes).map(key => {
                checkboxes[key] = areAllCollapsed;
            });
            areAllCollapsed = !areAllCollapsed;
        }

        tasks.map(task => {
            if (taskId) {
                if (found) {
                    if (checkboxKeys.includes(task.id)) {
                        found = false;
                    }
                    if (task.canCollapse) {
                        collapsed[task.id] = !collapsed[task.id];
                    }
                }
                if (task.id === taskId) {
                    found = true;
                }
            } else {
                if (task.canCollapse) {
                    collapsed[task.id] = areAllCollapsed;
                }
            }
        });
        this.setState({collapsed, checkboxes, areAllCollapsed});
    }

    updateWindowDimensions() {
        this.setState({width: window.innerWidth - OVERFLOW_TABLE_WIDTH});
    }

    handleSave({e, dateProps, checkboxProps, priceProps, percentProps, excludedProps, tableCode, tasks}) {
        e.preventDefault();
        this.saveData({dateProps, checkboxProps, priceProps, percentProps, excludedProps, tableCode, tasks});
    }

    saveData({dateProps, checkboxProps, priceProps, percentProps, excludedProps, tableCode, tasks}) {
        if (this.state.isBusy) return;

        this.setState({isBusy: true});

        let data = convertToBackendFormat({dateProps, checkboxProps, data: this.state.data});

        // Discard unchanged values and erase additional data
        data.map((row, index) => {
            const id = row.ARCIMEDES_ROW_ID;
            Object.keys(row).forEach(col => {
                if (excludedProps && excludedProps.includes(col) || row[col] === this.state.original[index][col]) {
                    delete row[col];
                }
            });
            if (Object.keys(row).length) {
                row.ARCIMEDES_ROW_ID = id;
            }
        });

        data = data.filter(row => Object.keys(row).length);

        // No changes detected, nothing to do
        if (!data.length) {
            this.setState({
                isBusy: null,
                error: null,
                success: 'No data to update',
            });
            return;
        } else {
            this.setState({
                isBusy: true,
                error: null,
                success: null,
            });
        }

        return request(arc.path.api, {
            service: 'arcimedes',
            action: 'open.datasource.table.Data.updateByTableCode',
            params: [tableCode, {
                value: data
            }],
        }).then(res => {
            if (res && res.success) {
                if (tableCode === STRIVE_ESCROW_TRACKING)
                    this.saveEffectiveDate(data, this.state.original, tasks);
                this.fetch({propertyId: this.props.location.state, dateProps, checkboxProps, priceProps, percentProps, saved: true});
            } else {
                this.setState({
                    isBusy: null,
                    error: res && res.message ? res.message : 'Unspecified error',
                });
            }
        });
    }

    saveEffectiveDate(data, original, tasks) {
        const tasksToSave = tasks.filter(task => task.saveToProperty);
        const propertiesToSave = [];
        let index = 0;
        data.map((row, dataIndex) => {
            tasksToSave.map(task => {
                if (row[task.id]) {
                    if (!propertiesToSave[index])
                        propertiesToSave[index] = {
                            ARCIMEDES_ROW_ID: original[dataIndex].PROPERTY_ID,
                        };
                    propertiesToSave[index][task.id] = row[task.id];
                    index++;
                }
            });
        });

        if (propertiesToSave.length)
            request(arc.path.api, {
                service: 'arcimedes',
                action: 'open.datasource.table.Data.updateByTableCode',
                params: [PROPERTIES, {
                    value: propertiesToSave
                }],
            });
    }

    handleChange(e) {
        const name = e.currentTarget.name.split('-');
        const row = name[0];
        const col = name[1];
        this.setData(row, col, e.currentTarget.value);
    }

    handleChangeDropdown(query, queryType, x, key, key2, propertyId) {
        this.setData(propertyId, key, query[key], key2, query[key2]);
    }

    setData(row, col, value, col2, value2) {
        const data = this.state.data;
        const rowIndex = findIndex(data, (obj) => {return obj.ARCIMEDES_ROW_ID === row});
        if (row && col && !col.includes('_CHECKBOX')) {
            data[rowIndex][col] = value;
            if (col2) data[rowIndex][col2] = value2;
        }

        if (col === 'EFFECTIVE_DATE' || col === 'FEASIBILITY_PERIOD' || col === 'FEASIBILITY_EXTENSION') {
            const effectiveDate = moment(data[rowIndex].EFFECTIVE_DATE);
            const period = parseInt(data[rowIndex].FEASIBILITY_PERIOD);
            const extension = data[rowIndex].FEASIBILITY_EXTENSION ? parseInt(data[rowIndex].FEASIBILITY_EXTENSION) : 0;
            const feasibilityDate = calculateDate(effectiveDate, period, extension);
            if (effectiveDate && period) {
                data[rowIndex].FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE = feasibilityDate;
            } else {
                data[rowIndex].FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE = '';
            }
        }

        if (col === 'EFFECTIVE_DATE' || col === 'CLOSING_PERIOD' || col === 'CLOSING_EXTENSION') {
            const effectiveDate = moment(data[rowIndex].EFFECTIVE_DATE);
            const period = parseInt(data[rowIndex].CLOSING_PERIOD);
            const extension = data[rowIndex].CLOSING_EXTENSION ? parseInt(data[rowIndex].CLOSING_EXTENSION) : 0;
            const closingDate = calculateDate(effectiveDate, period, extension);
            if (effectiveDate && period) {
                data[rowIndex].CLOSING_DATE = closingDate;
            } else {
                data[rowIndex].CLOSING_DATE = '';
            }
        }

        if (col.includes('_CHECKBOX'))
            data[rowIndex][col] = !data[rowIndex][col];

        this.setState({data});
    }

    addNewItem(addNewItemKey, addNewItemType, index) {
        this.setState({addNewItemKey, addNewItemType, index}, this.toggleDrawer);
    }

    toggleDrawer() {
        this.setState((prevState) => ({showDrawer: !prevState.showDrawer}));
    }

    closeDrawer() {
        this.setState({showDrawer: false});
    }

    savedNewItem(newData) {
        // select the new item id & close drawer
        const key = this.state.addNewItemKey;
        const type = this.state.addNewItemType;
        const index = this.state.index;
        const state = {
            data: cloneDeep(this.state.data),
        };

        state.data[index][key] = newData.ARCIMEDES_ROW_ID;
        state.data[index]['BORROWER_TYPE'] = type;

        switch (type) {
            case 'buyer':
                state.newBuyer = {...newData, groupedBy: type};
                break;
            case "seller":
                state.newSeller = {...newData, groupedBy: type};
                break;
        }
        this.setState(state, () => this.toggleDrawer());
    }

    getDrawerComponent() {
        const component = this.state.addNewItemType;

        switch (component) {
            case 'buyer':
                return (
                    <BuyerDrawer
                        savedNewItem={this.savedNewItem}
                        closeDrawer={this.closeDrawer}
                    />
                );
            case 'seller':
                return (
                    <SellerDrawer
                        savedNewItem={this.savedNewItem}
                        closeDrawer={this.closeDrawer}
                    />
                );
        }
    }

    renderHeader({date, tasks, dateProps, checkboxProps, priceProps, percentProps, excludedProps, tableCode, areAllCollapsed = null}) {
        return (
            <React.Fragment>
                <h3 className='Tracking_h3'>{this.state.title}</h3>
                <h4 className='Tracking_h4'>{date}</h4>
                <button className='Tracking_save' data-busy={this.state.isBusy} onClick={(e) => this.handleSave({e, dateProps, checkboxProps, priceProps, percentProps, excludedProps, tableCode, tasks})}>Save</button>
                { areAllCollapsed !== null ? <button className='Tracking_save' data-busy={this.state.isBusy} onClick={(e) => this.handleCollapse(e, null, tasks)}>{areAllCollapsed ? 'Expand' : 'Collapse'} Rows</button> : null }
                { this.state.success ? <Alert type='success'>{this.state.success}</Alert> : null }
                { this.state.error ? <Alert type='error'>{this.state.error}</Alert> : null }
            </React.Fragment>
        )
    }

    renderTableElement(task, element, isGray) {
        switch (task.type) {
            case 'date':
                return (
                    <div className="Tracking_date_wrapper">
                        <Input
                            theme={task.highlighted ? 'highlighted_without_border' : (isGray ? 'gray_without_border' : 'without_border')}
                            className="Tracking_td" type='text' name={`${element.ARCIMEDES_ROW_ID}-${task.id}`}
                            value={element[task.id]} onChange={this.handleChange} placeholder='MM/DD/YYYY'
                            title='MM/DD/YYYY'
                            pattern='(0?[1-9]|1[012])[\/](0?[1-9]|[12][0-9]|3[01])[\/]([1|2]\d\d\d)'/>
                        {
                            element[task.id + '_CHECKBOX'] ?
                                <input name={`${element.ARCIMEDES_ROW_ID}-${task.id}_CHECKBOX`}
                                       className="Tracking_date_checkbox" type="checkbox" checked
                                       onChange={this.handleChange}/>
                                :
                                <input name={`${element.ARCIMEDES_ROW_ID}-${task.id}_CHECKBOX`}
                                       className="Tracking_date_checkbox" type="checkbox" onChange={this.handleChange}/>
                        }
                    </div>
                );
            case 'dropdown':
                return <Select theme={isGray ? 'gray_without_border' : 'without_border'}
                               name={`${element.ARCIMEDES_ROW_ID}-${task.id}`} value={element[task.id]}
                               onChange={this.handleChange} placeholder='Yes/No'>
                    <option value=""/>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                </Select>;
            case 'text':
                return <Input theme={isGray ? 'gray_without_border' : 'without_border'} className="Tracking_td"
                              type='text' name={`${element.ARCIMEDES_ROW_ID}-${task.id}`} value={element[task.id]}
                              onChange={this.handleChange}/>;
            case 'static':
                return <div>{element[task.id]}</div>;
        }
    }

    renderTable({data, tasks, checkboxes, checkboxKeys, collapsed, isOneProperty = false}) {
        if (data === false) return <div className='Tracking_no_data'>No data found</div>;

        return (
            <div style={{width: isOneProperty ? `${width}%` : this.state.width}}
                 className={(this.state.success || this.state.error) ? 'Tracking_scroll_container Tracking_smaller_height' : 'Tracking_scroll_container'}>
                <table className="Tracking_table">
                    <thead>
                    <tr>
                        <th className="Tracking_th with_padding Tracking_border_top Tracking_border_left"
                            id="Tracking_task_cell">
                            Task {data.length > 1 ? `- ${data.length} Properties in Escrow` : ''}
                        </th>
                        {
                            data.map(row => {
                                const index = row.TASK.lastIndexOf('-');
                                const name = row.TASK ? row.TASK.slice(0, index - 1) : 'N/A';
                                const city = row.TASK ? row.TASK.slice(index + 2, row.TASK.length) : 'N/A';
                                return (
                                    <th key={row.ARCIMEDES_ROW_ID}
                                        className="Tracking_th Tracking_center Tracking_border_top">
                                        <Link to={{pathname: '/property/' + row.PROPERTY_ID}}>
                                            {name}<br/>{city}
                                        </Link>
                                    </th>
                                );
                            })
                        }
                    </tr>
                    </thead>
                    <tbody>
                    {
                        tasks.map((task, taskIndex) => {
                            return (
                                <tr key={task.id}>
                                    <th className={`Tracking_th ${task.isSticky ? 'sticky' : ''} ${task.isSubTask ? 'subtask' : 'with_padding'} Tracking_border_left${task.highlighted ? ' Tracking_highlighted' : ''} ${collapsed && collapsed[task.id] ? 'Tracking_hidden_row' : 'Tracking_visible_row'}`}
                                        style={{top: task.isSticky ? (this.state.width > limitWidth ? (headerCellHeight + taskIndex * cellHeight) : (headerCellHeightSmall + taskIndex * cellHeightSmall)) : 0}}>
                                        {task.step && `${task.step}. `}{task.name}
                                        {checkboxKeys.includes(task.id) ?
                                            <div className={'Tracking_eye'}
                                                 dangerouslySetInnerHTML={{__html: (checkboxes[task.id] ? arrowDown : arrowRight)}}
                                                 onClick={(e) => this.handleCollapse(e, task.id, tasks)}/>
                                            : ''
                                        }
                                    </th>
                                    {
                                        data ? data.map((element, index) => {
                                            const isGray = index % 2;
                                            return (
                                                <td key={element.ARCIMEDES_ROW_ID}
                                                    style={{top: task.isSticky ? (this.state.width > limitWidth ? (headerCellHeight + taskIndex * cellHeight) : (headerCellHeightSmall + taskIndex * cellHeightSmall)) : 0}}
                                                    className={`Tracking_td ${task.isSticky ? 'sticky' : ''}${task.highlighted ? ' Tracking_highlighted' : isGray ? ' Tracking_gray' : ''}${collapsed && collapsed[task.id] ? ' Tracking_hidden_row' : ' Tracking_visible_row'}`}>
                                                    {this.renderTableElement(task, element, isGray)}
                                                </td>
                                            );
                                        }) : ''
                                    }
                                </tr>
                            );
                        })
                    }
                    </tbody>
                </table>
            </div>
        )
    }
}